import { useState } from "react";
import api from "../../services/api";

function fmt(n) { return (n || 0).toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 }); }
function fmtDate(d) { if (!d) return "—"; return new Date(d).toLocaleDateString("fr-FR"); }

const STATUS_CONFIG = {
  actif:       { label: "Actif",      color: "#1a9e72", bg: "#1a9e7222" },
  suspendu:    { label: "Suspendu",   color: "#64748b", bg: "#64748b22" },
  resilie:     { label: "Résilié",    color: "#e24b4a", bg: "#e24b4a22" },
  en_attente:  { label: "En attente", color: "#ba7517", bg: "#ba751722" },
};

const FREQ_LABEL = {
  mensuel:     "Mensuel",
  trimestriel: "Trimestriel",
  semestriel:  "Semestriel",
  annuel:      "Annuel",
  personnalise: "Personnalisé",
};

const FREQ_ICON = {
  mensuel: "📅", trimestriel: "📅", semestriel: "📅", annuel: "📅", personnalise: "⚙️",
};

const FREQ_OPTIONS = [
  { value: "mensuel",      label: "📅 Mensuel",       desc: "1 paiement par mois" },
  { value: "trimestriel",  label: "📅 Trimestriel",    desc: "1 paiement tous les 3 mois" },
  { value: "semestriel",   label: "📅 Semestriel",     desc: "1 paiement tous les 6 mois" },
  { value: "annuel",       label: "📅 Annuel",         desc: "1 paiement par an" },
  { value: "personnalise", label: "⚙️ Personnalisé",   desc: "Intervalle sur mesure" },
];

const PAYMENT_LABEL = {
  virement: "Virement", prelevement: "Prélèvement", carte: "Carte", paypal: "PayPal",
};

const REMINDER_OPTIONS = [
  { days: "30", label: "30 jours" },
  { days: "15", label: "15 jours" },
  { days: "7",  label: "7 jours" },
  { days: "3",  label: "3 jours" },
];

const EMPTY_FORM = {
  customer_name: "", customer_email: "", name: "", description: "",
  amount_ht: "", tva_rate: "20", frequency: "mensuel",
  interval_days: "", start_date: new Date().toISOString().split("T")[0],
  end_date: "", payment_method: "virement", annual_discount: "",
  auto_invoice: true, reminder_days: ["7"], notes: "",
};

export default function ListTab({ currentCompanyId, subs, onRefresh }) {
  const [filterStatus, setFilterStatus]   = useState("all");
  const [filterFreq, setFilterFreq]       = useState("all");
  const [filterDue, setFilterDue]         = useState("all");
  const [showModal, setShowModal]         = useState(false);
  const [editSub, setEditSub]             = useState(null);
  const [form, setForm]                   = useState(EMPTY_FORM);
  const [saving, setSaving]               = useState(false);
  const [error, setError]                 = useState("");
  const [confirm, setConfirm]             = useState(null);

  const today = new Date().toISOString().split("T")[0];
  const in7   = new Date(); in7.setDate(in7.getDate() + 7);
  const in30  = new Date(); in30.setDate(in30.getDate() + 30);

  const filtered = subs.filter(s => {
    if (filterStatus !== "all" && s.status !== filterStatus) return false;
    if (filterFreq   !== "all" && s.frequency !== filterFreq) return false;
    if (filterDue === "7"  && new Date(s.next_billing_date) > in7)  return false;
    if (filterDue === "30" && new Date(s.next_billing_date) > in30) return false;
    return true;
  });

  function openNew() {
    setEditSub(null);
    setForm(EMPTY_FORM);
    setError("");
    setShowModal(true);
  }

  function openEdit(s) {
    setEditSub(s);
    const remDays = s.reminder_days
      ? s.reminder_days.toString().split(",").map(d => d.trim()).filter(Boolean)
      : ["7"];
    setForm({
      customer_name:   s.customer_name   || "",
      customer_email:  s.customer_email  || "",
      name:            s.name            || "",
      description:     s.description     || "",
      amount_ht:       s.amount_ht       || "",
      tva_rate:        s.tva_rate        || "20",
      frequency:       s.frequency       || "mensuel",
      interval_days:   s.interval_days   || "",
      start_date:      s.start_date      || today,
      end_date:        s.end_date        || "",
      payment_method:  s.payment_method  || "virement",
      annual_discount: s.annual_discount || "",
      auto_invoice:    !!s.auto_invoice,
      reminder_days:   remDays,
      notes:           s.notes           || "",
    });
    setError("");
    setShowModal(true);
  }

  async function handleSave() {
    if (!form.customer_name || !form.name || !form.amount_ht || !form.start_date) {
      setError("Veuillez remplir tous les champs obligatoires.");
      return;
    }
    setSaving(true);
    setError("");
    try {
      const payload = {
        ...form,
        company_id: currentCompanyId,
        reminder_days: Array.isArray(form.reminder_days) ? form.reminder_days.join(",") : form.reminder_days,
      };
      if (editSub) {
        await api.subscriptions.update(editSub.id, payload);
      } else {
        await api.subscriptions.create(payload);
      }
      setShowModal(false);
      onRefresh();
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  }

  async function handleAction(action, sub) {
    try {
      if (action === "invoice")  await api.subscriptions.generateInvoice(sub.id);
      if (action === "suspend")  await api.subscriptions.suspend(sub.id);
      if (action === "cancel")   await api.subscriptions.cancel(sub.id);
      if (action === "delete")   await api.subscriptions.delete(sub.id);
      setConfirm(null);
      onRefresh();
    } catch (e) {
      alert(e.message);
    }
  }

  function set(field, val) { setForm(f => ({ ...f, [field]: val })); }

  function toggleReminder(day) {
    setForm(f => {
      const current = Array.isArray(f.reminder_days) ? f.reminder_days : [String(f.reminder_days)];
      return {
        ...f,
        reminder_days: current.includes(day) ? current.filter(d => d !== day) : [...current, day],
      };
    });
  }

  return (
    <div>
      {/* Toolbar */}
      <div style={{ display: "flex", gap: 8, marginBottom: 16, flexWrap: "wrap", alignItems: "center" }}>
        <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} className="input" style={{ width: "auto" }}>
          <option value="all">Tous statuts</option>
          {Object.entries(STATUS_CONFIG).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
        </select>
        <select value={filterFreq} onChange={e => setFilterFreq(e.target.value)} className="input" style={{ width: "auto" }}>
          <option value="all">Toutes fréquences</option>
          {Object.entries(FREQ_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
        <select value={filterDue} onChange={e => setFilterDue(e.target.value)} className="input" style={{ width: "auto" }}>
          <option value="all">Toutes échéances</option>
          <option value="7">Échéance 7 jours</option>
          <option value="30">Échéance 30 jours</option>
        </select>
        <div style={{ flex: 1 }} />
        <button className="btn primary" onClick={openNew}>+ Nouvel abonnement</button>
      </div>

      {/* Résumé filtre */}
      <div style={{ fontSize: 12, color: "var(--text-muted)", marginBottom: 12 }}>
        {filtered.length} abonnement(s)
      </div>

      {/* Tableau */}
      <div className="card" style={{ padding: 0, overflow: "hidden" }}>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr style={{ background: "var(--bg-secondary, #f8fafc)", fontSize: 11, color: "var(--text-muted)", textTransform: "uppercase" }}>
              <th style={{ padding: "10px 14px", textAlign: "left", fontWeight: 600 }}>Client / Offre</th>
              <th style={{ padding: "10px 14px", textAlign: "right", fontWeight: 600 }}>Montant TTC</th>
              <th style={{ padding: "10px 14px", textAlign: "left", fontWeight: 600 }}>Périodicité</th>
              <th style={{ padding: "10px 14px", textAlign: "left", fontWeight: 600 }}>Prochaine échéance</th>
              <th style={{ padding: "10px 14px", textAlign: "left", fontWeight: 600 }}>Statut</th>
              <th style={{ padding: "10px 14px", textAlign: "right", fontWeight: 600 }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr>
                <td colSpan={6} style={{ textAlign: "center", padding: 40, color: "var(--text-muted)", fontSize: 13 }}>
                  Aucun abonnement trouvé
                </td>
              </tr>
            ) : filtered.map(s => {
              const sc = STATUS_CONFIG[s.status] || STATUS_CONFIG.en_attente;
              const daysUntil = s.next_billing_date
                ? Math.ceil((new Date(s.next_billing_date) - new Date()) / 86400000)
                : null;
              const isOverdue = daysUntil !== null && daysUntil < 0 && s.status === "actif";
              const isUrgent  = daysUntil !== null && daysUntil >= 0 && daysUntil <= 7 && s.status === "actif";
              const hasDiscount = s.annual_discount && parseFloat(s.annual_discount) > 0;

              return (
                <tr key={s.id} style={{ borderTop: "var(--border)", fontSize: 13 }}>
                  <td style={{ padding: "11px 14px" }}>
                    <div style={{ fontWeight: 600 }}>{s.customer_name}</div>
                    <div style={{ fontSize: 11, color: "var(--text-muted)" }}>{s.name}</div>
                    {s.description && (
                      <div style={{ fontSize: 10, color: "var(--text-muted)", marginTop: 2, fontStyle: "italic" }}>{s.description}</div>
                    )}
                  </td>
                  <td style={{ padding: "11px 14px", textAlign: "right", fontWeight: 700 }}>
                    {fmt(s.amount_ttc)} €
                    <div style={{ fontSize: 10, color: "var(--text-muted)", fontWeight: 400 }}>{fmt(s.amount_ht)} € HT</div>
                  </td>
                  <td style={{ padding: "11px 14px" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                      <span style={{ fontSize: 13 }}>{FREQ_ICON[s.frequency] || "📅"}</span>
                      <span>{FREQ_LABEL[s.frequency] || s.frequency}</span>
                    </div>
                    {hasDiscount && (
                      <span style={{ fontSize: 10, padding: "2px 6px", borderRadius: 6, background: "#1a9e7222", color: "#1a9e72", fontWeight: 700, display: "inline-block", marginTop: 3 }}>
                        Annuel -{s.annual_discount}%
                      </span>
                    )}
                    {s.payment_method && (
                      <div style={{ fontSize: 10, color: "var(--text-muted)", marginTop: 2 }}>{PAYMENT_LABEL[s.payment_method]}</div>
                    )}
                  </td>
                  <td style={{ padding: "11px 14px" }}>
                    {isOverdue ? (
                      <>
                        <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
                          <span>🔴</span>
                          <span style={{ color: "#e24b4a", fontWeight: 700 }}>{fmtDate(s.next_billing_date)}</span>
                        </div>
                        <div style={{ fontSize: 10, color: "#e24b4a", marginTop: 2 }}>En retard de {Math.abs(daysUntil)}j</div>
                      </>
                    ) : isUrgent ? (
                      <>
                        <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
                          <span>🟠</span>
                          <span style={{ color: "#ba7517", fontWeight: 700 }}>{fmtDate(s.next_billing_date)}</span>
                        </div>
                        <div style={{ fontSize: 10, color: "#ba7517", marginTop: 2 }}>Dans {daysUntil}j</div>
                      </>
                    ) : (
                      <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
                        {s.status === "actif" && <span>🟢</span>}
                        <span>{fmtDate(s.next_billing_date)}</span>
                      </div>
                    )}
                  </td>
                  <td style={{ padding: "11px 14px" }}>
                    <span style={{ fontSize: 11, padding: "3px 8px", borderRadius: 8, fontWeight: 700, background: sc.bg, color: sc.color }}>
                      {sc.label}
                    </span>
                  </td>
                  <td style={{ padding: "11px 14px", textAlign: "right" }}>
                    <div style={{ display: "flex", gap: 6, justifyContent: "flex-end", flexWrap: "wrap" }}>
                      <button className="btn" style={{ fontSize: 11, padding: "4px 8px" }} onClick={() => openEdit(s)} title="Modifier">✏️</button>
                      {s.status === "actif" && (
                        <>
                          <button className="btn" style={{ fontSize: 11, padding: "4px 8px", color: "var(--g)", borderColor: "var(--g)" }}
                            onClick={() => setConfirm({ action: "invoice", sub: s })} title="Générer facture">🧾</button>
                          <button className="btn" style={{ fontSize: 11, padding: "4px 8px", color: "#64748b" }}
                            onClick={() => setConfirm({ action: "suspend", sub: s })} title="Suspendre">⏸</button>
                        </>
                      )}
                      {(s.status === "actif" || s.status === "suspendu") && (
                        <button className="btn" style={{ fontSize: 11, padding: "4px 8px", color: "#e24b4a", borderColor: "#e24b4a33" }}
                          onClick={() => setConfirm({ action: "cancel", sub: s })} title="Résilier">🚫</button>
                      )}
                      <button className="btn" style={{ fontSize: 11, padding: "4px 8px", color: "#e24b4a" }}
                        onClick={() => setConfirm({ action: "delete", sub: s })} title="Supprimer">🗑</button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Confirm dialog */}
      {confirm && (
        <div style={{ position: "fixed", inset: 0, background: "#0008", zIndex: 999, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <div className="card" style={{ maxWidth: 380, width: "90%" }}>
            <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 12 }}>
              {confirm.action === "invoice"  && "Générer une facture"}
              {confirm.action === "suspend"  && "Suspendre l'abonnement"}
              {confirm.action === "cancel"   && "Résilier l'abonnement"}
              {confirm.action === "delete"   && "Supprimer l'abonnement"}
            </div>
            <div style={{ color: "var(--text-muted)", marginBottom: 20, fontSize: 13 }}>
              {confirm.action === "invoice"  && `Générer une facture de ${fmt(confirm.sub.amount_ttc)} € TTC pour ${confirm.sub.customer_name} ?`}
              {confirm.action === "suspend"  && `Suspendre l'abonnement "${confirm.sub.name}" de ${confirm.sub.customer_name} ?`}
              {confirm.action === "cancel"   && `Résilier définitivement l'abonnement "${confirm.sub.name}" ?`}
              {confirm.action === "delete"   && `Supprimer définitivement l'abonnement "${confirm.sub.name}" ? Cette action est irréversible.`}
            </div>
            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
              <button className="btn" onClick={() => setConfirm(null)}>Annuler</button>
              <button className="btn primary" style={{ background: confirm.action === "delete" ? "#e24b4a" : undefined }}
                onClick={() => handleAction(confirm.action, confirm.sub)}>Confirmer</button>
            </div>
          </div>
        </div>
      )}

      {/* Modal création / modification */}
      {showModal && (
        <div style={{ position: "fixed", inset: 0, background: "#0008", zIndex: 999, display: "flex", alignItems: "flex-start", justifyContent: "center", overflowY: "auto", padding: "40px 16px" }}>
          <div className="card" style={{ width: "100%", maxWidth: 560 }}>
            <div style={{ display: "flex", alignItems: "center", marginBottom: 20 }}>
              <span style={{ fontWeight: 700, fontSize: 16 }}>{editSub ? "Modifier l'abonnement" : "Nouvel abonnement"}</span>
              <button className="btn" style={{ marginLeft: "auto" }} onClick={() => setShowModal(false)}>✕</button>
            </div>

            {error && <div style={{ background: "#e24b4a22", color: "#e24b4a", padding: "8px 12px", borderRadius: 8, marginBottom: 12, fontSize: 13 }}>{error}</div>}

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>

              {/* Client */}
              <div style={{ gridColumn: "1/-1" }}>
                <label style={{ fontSize: 12, fontWeight: 600, display: "block", marginBottom: 4 }}>Client *</label>
                <input className="input" value={form.customer_name} onChange={e => set("customer_name", e.target.value)} placeholder="Nom du client" />
              </div>
              <div style={{ gridColumn: "1/-1" }}>
                <label style={{ fontSize: 12, fontWeight: 600, display: "block", marginBottom: 4 }}>Email client</label>
                <input className="input" type="email" value={form.customer_email} onChange={e => set("customer_email", e.target.value)} placeholder="email@client.com" />
              </div>

              {/* Offre */}
              <div style={{ gridColumn: "1/-1" }}>
                <label style={{ fontSize: 12, fontWeight: 600, display: "block", marginBottom: 4 }}>Nom de l'offre / service *</label>
                <input className="input" value={form.name} onChange={e => set("name", e.target.value)} placeholder="Ex : Abonnement Pro, Licence SaaS…" />
              </div>
              <div style={{ gridColumn: "1/-1" }}>
                <label style={{ fontSize: 12, fontWeight: 600, display: "block", marginBottom: 4 }}>Description</label>
                <textarea className="input" rows={2} value={form.description} onChange={e => set("description", e.target.value)}
                  placeholder="Détails de l'offre, fonctionnalités incluses…" style={{ resize: "vertical" }} />
              </div>

              {/* Montants */}
              <div>
                <label style={{ fontSize: 12, fontWeight: 600, display: "block", marginBottom: 4 }}>Montant HT (€) *</label>
                <input className="input" type="number" step="0.01" value={form.amount_ht} onChange={e => set("amount_ht", e.target.value)} placeholder="0.00" />
              </div>
              <div>
                <label style={{ fontSize: 12, fontWeight: 600, display: "block", marginBottom: 4 }}>TVA (%)</label>
                <input className="input" type="number" step="0.1" value={form.tva_rate} onChange={e => set("tva_rate", e.target.value)} />
              </div>
              {form.amount_ht && (
                <div style={{ gridColumn: "1/-1", fontSize: 12, color: "var(--g)", background: "var(--g)11", padding: "6px 10px", borderRadius: 6 }}>
                  Montant TTC : <strong>{(parseFloat(form.amount_ht || 0) * (1 + parseFloat(form.tva_rate || 0) / 100)).toLocaleString("fr-FR", { minimumFractionDigits: 2 })} €</strong>
                </div>
              )}

              {/* Périodicité + remise */}
              <div>
                <label style={{ fontSize: 12, fontWeight: 600, display: "block", marginBottom: 4 }}>Périodicité *</label>
                <select className="input" value={form.frequency} onChange={e => set("frequency", e.target.value)}>
                  {FREQ_OPTIONS.map(o => (
                    <option key={o.value} value={o.value}>{o.label} — {o.desc}</option>
                  ))}
                </select>
              </div>
              <div>
                <label style={{ fontSize: 12, fontWeight: 600, display: "block", marginBottom: 4 }}>Remise annuelle (%)</label>
                <input className="input" type="number" step="0.1" min="0" max="100"
                  value={form.annual_discount} onChange={e => set("annual_discount", e.target.value)} placeholder="0" />
              </div>
              {form.frequency === "personnalise" && (
                <div style={{ gridColumn: "1/-1" }}>
                  <label style={{ fontSize: 12, fontWeight: 600, display: "block", marginBottom: 4 }}>Intervalle (jours)</label>
                  <input className="input" type="number" value={form.interval_days} onChange={e => set("interval_days", e.target.value)} placeholder="30" />
                </div>
              )}

              {/* Dates */}
              <div>
                <label style={{ fontSize: 12, fontWeight: 600, display: "block", marginBottom: 4 }}>Date de début *</label>
                <input className="input" type="date" value={form.start_date} onChange={e => set("start_date", e.target.value)} />
              </div>
              <div>
                <label style={{ fontSize: 12, fontWeight: 600, display: "block", marginBottom: 4 }}>Date de fin</label>
                <input className="input" type="date" value={form.end_date} onChange={e => set("end_date", e.target.value)} />
              </div>

              {/* Paiement */}
              <div style={{ gridColumn: "1/-1" }}>
                <label style={{ fontSize: 12, fontWeight: 600, display: "block", marginBottom: 4 }}>Mode de paiement</label>
                <select className="input" value={form.payment_method} onChange={e => set("payment_method", e.target.value)}>
                  {Object.entries(PAYMENT_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                </select>
              </div>

              {/* Rappels multi-checkbox */}
              <div style={{ gridColumn: "1/-1" }}>
                <label style={{ fontSize: 12, fontWeight: 600, display: "block", marginBottom: 8 }}>Rappels avant échéance</label>
                <div style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>
                  {REMINDER_OPTIONS.map(({ days, label }) => {
                    const current = Array.isArray(form.reminder_days) ? form.reminder_days : [String(form.reminder_days)];
                    return (
                      <label key={days} style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13, cursor: "pointer" }}>
                        <input type="checkbox" checked={current.includes(days)} onChange={() => toggleReminder(days)} />
                        {label}
                      </label>
                    );
                  })}
                </div>
              </div>

              {/* Auto-facture */}
              <div style={{ gridColumn: "1/-1", display: "flex", alignItems: "center", gap: 8, padding: "4px 0" }}>
                <input type="checkbox" id="auto_invoice" checked={form.auto_invoice} onChange={e => set("auto_invoice", e.target.checked)} />
                <label htmlFor="auto_invoice" style={{ fontSize: 13, cursor: "pointer" }}>Générer la facture automatiquement à chaque échéance</label>
              </div>

              {/* Notes */}
              <div style={{ gridColumn: "1/-1" }}>
                <label style={{ fontSize: 12, fontWeight: 600, display: "block", marginBottom: 4 }}>Notes</label>
                <textarea className="input" rows={2} value={form.notes} onChange={e => set("notes", e.target.value)}
                  placeholder="Informations complémentaires…" style={{ resize: "vertical" }} />
              </div>
            </div>

            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 20 }}>
              <button className="btn" onClick={() => setShowModal(false)} disabled={saving}>Annuler</button>
              <button className="btn primary" onClick={handleSave} disabled={saving}>
                {saving ? "Enregistrement…" : editSub ? "Mettre à jour" : "Créer l'abonnement"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
